import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as styles from './ReuseTemplateModal.module.scss';
import Dialog from 'system/base/Dialog';
import { PrimaryButton, TertiaryButton } from 'system/base/Button';
import { Text, Link, Loading } from 'components/core';
import { Stack } from 'components/layout';
import { CopyableReviewList } from 'components/shared';
import { fetchReviewsWithCopyableTemplates } from 'query/review';
import { ID } from 'util/localTypes';

export type ReuseTemplateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  reviewerId: ID;
};

export function ReuseTemplateModal({
  isOpen,
  onClose,
  reviewId,
  reviewerId,
}: ReuseTemplateModalProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [validationError, setValidationError] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const { isFetching, data, isError: fetchError } = useQuery({
    queryKey: ['reviewsWithCopyableTemplates', reviewId, reviewerId],
    queryFn: () => fetchReviewsWithCopyableTemplates(reviewId),
    enabled: isOpen,
    staleTime: 300_000, // 5 minutes
    select: (response) => response.source_reviews,
  });
  const reviews = data ?? null;

  const handleCopy = () => {
    if (!selectedId) {
      setValidationError(true);
      return;
    }
    setIsCopying(true);
    const currentParams = new URLSearchParams(window.location.search);
    const copyParams = new URLSearchParams({
      source_review_id: String(selectedId),
    });
    const backTo = currentParams.get('backTo');
    const type = currentParams.get('type');
    if (backTo) copyParams.set('backTo', backTo);
    if (type) copyParams.set('type', type);
    window.location.assign(
      `/reviews/${reviewId}/extraction/build/copy?${copyParams.toString()}`
    );
  };

  const isEmpty = reviews !== null && reviews.length === 0;

  return (
    <Dialog
      isOpen={isOpen}
      onDismiss={onClose}
      title="Select a review to copy template"
    >
      <Stack spacing={2}>
        <Text size="md" variant="default" component="p">
          Your current template will be replaced with a{' '}
          <Link
            to="https://support.covidence.org/help/copy-template-from-another-review"
            variant="primary"
            external
          >
            copy of the selected review&apos;s template.
          </Link>
          <br />
          Only published Extraction 2 templates can be copied.
        </Text>

        {isFetching && (
          <div className={styles.LoadingContainer}>
            <Loading size="lg" type="neutral" />
          </div>
        )}

        {fetchError && (
          <Text size="md" variant="danger" component="p">
            Failed to load reviews. Please try again.
          </Text>
        )}

        {reviews !== null && (
          <CopyableReviewList
            aria-label="Select a review to copy template from"
            reviews={reviews}
            onChange={(id) => {
              setSelectedId(id);
              setValidationError(false);
            }}
            error={
              validationError ? 'Select a review to copy template' : undefined
            }
          />
        )}
      </Stack>

      {isEmpty ? (
        <PrimaryButton
          mt={3}
          data-pendo-key="de2-reuse-template-empty-state"
          onClick={onClose}
        >
          Done
        </PrimaryButton>
      ) : (
        <div>
          <PrimaryButton
            mr={2}
            data-pendo-key="de2-reuse-template-copy"
            onClick={handleCopy}
            disabled={isCopying}
          >
            {isCopying ? 'Copying…' : 'Copy template'}
          </PrimaryButton>
          <TertiaryButton onClick={onClose}>Cancel</TertiaryButton>
        </div>
      )}
    </Dialog>
  );
}
